import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chapter } from '../types';
import { getGraphQLClient, GET_BOOK_CHAPTERS_TREE } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Button, Card, Spinner } from '../components/common';

type ChaptersTreeResponse = {
  book: {
    id: string;
    title: string;
    chapters: Chapter[];
  } | null;
};

export default function ChapterFocusPage() {
  const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bookId) return;
    try {
      const client = getGraphQLClient();
      const data = await client.request<ChaptersTreeResponse>(GET_BOOK_CHAPTERS_TREE, {
        id: bookId,
      });
      setChapters(data.book?.chapters ?? []);
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [bookId, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Spinner />
      </div>
    );
  }

  const chapter = chapters.find((c) => c.id === chapterId);

  if (!chapter) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <p className="text-red-600 mb-3">
            {t('chapterFocus.notFound', 'Chapter not found.')}
          </p>
          <Button onClick={() => navigate(`/books/${bookId}/refine`)}>
            {t('chaptersFirst.backToWorkspace', 'Back to workspace')}
          </Button>
        </Card>
      </div>
    );
  }

  const scenes = [...(chapter.scenes ?? [])].sort((a, b) => a.position - b.position);
  const totalWords = scenes.reduce((sum, s) => sum + (s.wordCount ?? 0), 0);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <header className="space-y-2">
        <button
          onClick={() => navigate(`/books/${bookId}/refine`)}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          ← {t('chaptersFirst.backToWorkspace', 'Back to workspace')}
        </button>
        <div className="text-sm text-gray-500">
          {t('chapterFocus.chapterLabel', 'Chapter')} {chapter.position + 1}
        </div>
        <h1 className="text-3xl font-bold">{chapter.title}</h1>
        {chapter.description && (
          <p className="text-gray-600 dark:text-gray-400">{chapter.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300 pt-2">
          <span>
            {scenes.length} {t('chaptersFirst.scenesLowercase', 'scenes')}
          </span>
          <span className="text-gray-400">·</span>
          <span>
            ~{totalWords.toLocaleString()} {t('chaptersFirst.words', 'words')}
          </span>
        </div>
      </header>

      {error && (
        <Card>
          <p className="text-red-600">{error}</p>
        </Card>
      )}

      <Card padding="lg">
        <h2 className="text-lg font-semibold mb-4">
          {t('chapterFocus.scenesTitle', 'Scenes in this chapter')}
        </h2>
        {scenes.length === 0 ? (
          <p className="text-gray-500 italic">
            {t('chaptersFirst.noScenesYet', 'No scenes yet for this chapter.')}
          </p>
        ) : (
          <ol className="space-y-2">
            {scenes.map((scene) => (
              <li
                key={scene.id}
                className="flex items-start justify-between gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 cursor-pointer"
                onClick={() => navigate(`/books/${bookId}/scenes/${scene.id}`)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">{scene.position + 1}.</span>
                    <h3 className="font-medium truncate">{scene.title}</h3>
                  </div>
                  {scene.body && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {scene.body.slice(0, 200)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 shrink-0">
                  <span>
                    ~{(scene.wordCount ?? 0).toLocaleString()} {t('chaptersFirst.words', 'words')}
                  </span>
                  {scene.locked && <span>🔒</span>}
                  {scene.previewStale && <span>⚠</span>}
                </div>
              </li>
            ))}
          </ol>
        )}
        <div className="mt-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              navigate(`/books/${bookId}/chapters/${chapter.id}/scenes-first/select`)
            }
          >
            {t('chaptersFirst.refineScenes', 'Refine scenes')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
